import { FC, ReactNode } from 'react';
import { Video, FileText, CheckCircle, MoveUpRight } from 'lucide-react';

interface StatCard {
  label: string;
  value: number;
  icon: ReactNode;
  iconBackground: string;
  iconForeground: string;
  href?: string;
}

interface StatsCardsProps {
  pendingHearings: number;
  pendingProjects: number;
  completedHearings: number;
  completedProjects: number;
  onNavigate?: (path: string) => void;
}

const LABEL_CLASS =
  'text-(--neutral-black-NB900) font-arvo font-bold [font-size:var(--fs-p3)]! lg:[font-size:var(--fs-p2)]! leading-tight';
const VALUE_CLASS = 'text-(--neutral-black-NB900) [font-size:var(--fs-l5)]!';

export const StatsCards: FC<StatsCardsProps> = ({
  pendingHearings,
  pendingProjects,
  completedHearings,
  completedProjects,
  onNavigate,
}) => {
  const cards: StatCard[] = [
    {
      label: 'Course Hearings',
      value: pendingHearings,
      icon: <Video className="w-6 h-6" />,
      iconBackground: 'bg-(--secondary-B100)/50',
      iconForeground: 'text-(--secondary-B1000)',
      href: '/course-hearings',
    },
    {
      label: 'Projects',
      value: pendingProjects,
      icon: <FileText className="w-6 h-6" />,
      iconBackground: 'bg-purple-50',
      iconForeground: 'text-purple-700',
      href: '/projects',
    },
    {
      label: 'Total Completed',
      value: completedHearings + completedProjects,
      icon: <CheckCircle className="w-6 h-6" />,
      iconBackground: 'bg-(--success-S200)',
      iconForeground: 'text-(--success-S700)',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((card) => {
        const isNavigable = !!card.href;
        const status = isNavigable ? 'Pending' : 'Completed';

        return (
          <div
            key={card.label}
            className="group relative flex items-center md:items-start gap-4 rounded-lg bg-(--neutral-white-NW1000) p-6 shadow-sm ring-1 ring-gray-300 ring-inset hover:shadow-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-inset md:flex-col"
          >
            <span
              className={`inline-flex rounded-lg p-3 ring-4 ring-(--neutral-white-NW1000) ${card.iconBackground} ${card.iconForeground}`}
            >
              {card.icon}
            </span>

            <div className="flex flex-col gap-2">
              {isNavigable ? (
                <span
                  onClick={() => onNavigate?.(card.href!)}
                  className={`${LABEL_CLASS} cursor-pointer hover:text-(--neutral-black-NB800) focus:outline-none`}
                >
                  <span aria-hidden="true" className="absolute inset-0" />
                  {card.label}
                </span>
              ) : (
                <span className={LABEL_CLASS}>{card.label}</span>
              )}

              <span className={VALUE_CLASS}>
                {status}: {card.value}
              </span>
            </div>

            {isNavigable && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
              >
                <MoveUpRight className="h-4 w-4 lg:h-6 lg:w-6" />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
